import { useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { MultiValue, SingleValue } from "react-select";
import { OptionValue } from "../../../types/component";
import { isSelectSingleValue } from "../../../core/utils";
import StyledSelect from "../../../components/styled-select";
import { TrashOutlined } from "../../../components/icons/trash-outlined";
import { CheckOutlined } from "../../../components/icons/check-outlined";
import { EditOutlined } from "../../../components/icons/edit-outlined";
import { SummaryAction } from "../../../redux/features/weekly-summary-slice";
import Input from "../../../components/input";
import Button from "../../../components/button";

const actionItems = [
  "HR needed",
  "Training identified",
  "Customer Engagement Required",
  "No Action",
  "Other",
];

interface Props {
  actions: SummaryAction[];
  setActions: (values: SummaryAction[]) => void;
}

export default function WeeklySummaryActionForm(props: Props) {
  const { actions, setActions } = props;

  const [edit, setEdit] = useState<SummaryAction | null>(null);

  return (
    <div className="flex flex-col rounded-lg space-y-4">
      {actions.map((action: SummaryAction) => (
        <div
          key={action.type}
          className="flex flex-col items-center space-y-4 p-4 bg-background-default border border-divider rounded-lg"
        >
          <div className="w-full flex flex-col space-y-4">
            <div>
              <label className="text-xs text-text-title">Type</label>
              {edit ? (
                <StyledSelect
                  menuPosition="fixed"
                  options={(actions.length > 1
                    ? actionItems
                    : ["Nil", ...actionItems]
                  ).map((action: string) => ({
                    value: action,
                    label: action,
                  }))}
                  isClearable={false}
                  value={{
                    value: edit.type,
                    label: edit.type,
                  }}
                  onChange={(
                    value: SingleValue<OptionValue> | MultiValue<OptionValue>
                  ) => {
                    if (value && isSelectSingleValue(value)) {
                      setEdit({
                        ...edit,
                        type: value.value as string,
                        detail: (value.value as string) === "Nil" ? "--" : "",
                      });
                    }
                  }}
                  exClassName="w-full"
                />
              ) : (
                <Input type="text" value={action.type} readonly />
              )}
            </div>
            <div>
              <label className="text-xs text-text-title">Detail</label>
              <Input
                type="text"
                inputType="textarea"
                rows={3}
                value={edit ? edit.detail : action.detail}
                readonly={edit?.type === "Nil" || edit === null}
                onChange={(event) => {
                  if (edit) {
                    setEdit({
                      ...edit,
                      detail: event.target.value,
                    });
                  }
                }}
                errorText={
                  edit
                    ? edit.detail === ""
                      ? "This field is required"
                      : undefined
                    : undefined
                }
              />
            </div>
          </div>
          <div className="w-full flex items-center justify-end space-x-2">
            <div
              className="p-2 rounded cursor-pointer hover:bg-gray-200"
              onClick={() => {
                if (edit === null) {
                  setEdit(action);
                } else if (edit.type && edit.detail) {
                  const editIndex = actions.findIndex(
                    (e) => e.type === action.type && e.detail === action.detail
                  );
                  if (editIndex !== -1) {
                    const tmp = [...actions];
                    tmp[editIndex] = {
                      ...tmp[editIndex],
                      type: edit.type,
                      detail: edit.detail,
                    };
                    setActions(tmp);
                    setEdit(null);
                  }
                }
              }}
            >
              {edit ? (
                <CheckOutlined className="h-4 w-4 text-success-main" />
              ) : (
                <EditOutlined className="h-4 w-4" />
              )}
            </div>
            <div
              className="p-2 rounded cursor-pointer hover:bg-gray-200"
              onClick={() => {
                const removedIndex = actions.findIndex(
                  (e) => e.type === action.type && e.detail === action.detail
                );
                if (removedIndex !== -1) {
                  const tmp = actions.filter(
                    (_, index) => index !== removedIndex
                  );
                  setActions(tmp);
                }
              }}
            >
              <TrashOutlined className="h-4 w-4 text-error-main" />
            </div>
          </div>
        </div>
      ))}
      {actions.findIndex((e) => e.type === "Nil") === -1 && (
        <Formik
          initialValues={{ type: "", detail: "" }}
          validationSchema={Yup.object().shape({
            type: Yup.string().required("This field is required"),
            detail: Yup.string().required("This field is required"),
          })}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            setSubmitting(false);
            setActions([
              ...actions,
              {
                type: values.type,
                detail: values.detail,
              },
            ]);
            resetForm();
          }}
        >
          {({ values, setValues, errors, handleChange, handleSubmit }) => (
            <Form
              noValidate
              autoComplete="off"
              onSubmit={handleSubmit}
              className="flex flex-col items-center space-y-4 border border-divider rounded-lg p-4"
            >
              <div className="w-full flex flex-col space-y-4">
                <div>
                  <label className="text-xs text-text-title">Type</label>
                  <div>
                    <StyledSelect
                      menuPosition="fixed"
                      options={(actions.length > 0
                        ? actionItems
                        : ["Nil", ...actionItems]
                      ).map((action: string) => ({
                        value: action,
                        label: action,
                      }))}
                      isClearable={false}
                      value={
                        values.type !== ""
                          ? {
                              value: values.type,
                              label: values.type,
                            }
                          : {
                              value: "",
                              label: "Select...",
                            }
                      }
                      onChange={(
                        value:
                          | SingleValue<OptionValue>
                          | MultiValue<OptionValue>
                      ) => {
                        if (value && isSelectSingleValue(value)) {
                          setValues({
                            ...values,
                            type: value.value as string,
                            detail:
                              (value.value as string) === "Nil" ? "--" : "",
                          });
                        }
                      }}
                      exClassName="w-full"
                    />
                    {errors.type && (
                      <p className="text-error-main text-xs">{errors.type}</p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-text-title">Detail</label>
                  <Input
                    type="text"
                    inputType="textarea"
                    rows={3}
                    id="detail"
                    value={values.detail}
                    onChange={handleChange}
                    errorText={errors.detail}
                    readonly={values.type === "Nil"}
                  />
                </div>
              </div>
              <Button
                type="submit"
                label="Add"
                exClassName="w-fit self-end"
                disabled={
                  Array.isArray(errors) ||
                  Object.values(errors).toString() != ""
                }
              />
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
}
